import { useEffect, useState } from 'react';
import Select from 'react-select';
import {
    BUSINESS_RULES_ELEMENTS,
    FAULT_OUTCOME_DEVELOPER_NAME,
    FAULT_OUTCOME_ELEMENTS,
    FAULT_OUTCOME_ID,
    MAP_ELEMENT_TYPES,
    ONE_OUTCOME_ELEMENTS,
    UI_ELEMENTS,
} from '../../../../constants/graph';
import type { MapElementRequestAPI } from '../../../../sources/graph';
import { getPage } from '../../../../sources/page';
import translations from '../../../../translations';
import type { MapElement, Outcome } from '../../../../types/graph';
import {
    applyApprovalOutcomeConstraints,
    getApprovalOutcomeType,
    getAutoAssignmentType,
    updateApprovalOutcomeAssignment,
} from '../../../../utils/approval';
import { getByID } from '../../../../utils/collection';
import { isNullOrEmpty, isNullOrWhitespace } from '../../../../utils/guard';
import { isEmptyGuid } from '../../../../utils/guid';
import { getSharedStyles } from '../../../../utils/select';
import { stringReplace } from '../../../../utils/string';
import ButtonDefault from '../../../buttons/ButtonDefault';
import ButtonPrimary from '../../../buttons/ButtonPrimary';
import FormGroup from '../../../generic/FormGroup';
import ModalBody from '../../../generic/modal/ModalBody';
import ModalFooter from '../../../generic/modal/ModalFooter';
import Toggle from '../../../inputs/Toggle';
import Loader from '../../../loader/Loader';
import { useNotificationDispatch } from '../../../notifications/NotificationProvider';
import { useMapElement } from '../contextProviders/MapElementProvider';
import BusinessRules from '../outcome/businessRules/BusinessRules';
import NameInput from './NameInput';

interface Props {
    outcome: Partial<Outcome> | null;
    onCancel: () => void;
    onSave: (mapElement: MapElementRequestAPI) => void;
}

const OutcomeDetails = ({ outcome, onCancel, onSave }: Props) => {
    const { mapElement, mapElements, setMapElement, setConfigTitle, container } =
        useMapElement() as {
            mapElement: MapElement | undefined;
            mapElements: MapElement[];
            setMapElement: (element: MapElement) => void;
            setConfigTitle: (title: string | null) => void;
            container: HTMLElement | null;
        };

    const dispatchNotification = useNotificationDispatch();

    const [pageComponentOptions, setPageComponentOptions] = useState<
        { value: string | null; label: string | null }[]
    >([]);
    const [pageComponentsLoading, setPageComponentsLoading] = useState(true);

    // OutcomeDetails needs a list of page components
    // that are used to populate a dropdown for specifying
    // if an outcome should placed with a page component
    const fetchPageComponents = async (pageId: string) => {
        try {
            const pageElementResponse = await getPage(pageId);

            const parsedResponse = pageElementResponse;
            setPageComponentOptions(
                parsedResponse?.pageComponents?.map(({ id, developerName }) => ({
                    value: id,
                    label: developerName,
                })) || [],
            );
            setPageComponentsLoading(false);
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: Treat warnings as errors, fix later
    useEffect(() => {
        if (mapElement?.pageElementId) {
            fetchPageComponents(mapElement?.pageElementId);
        } else {
            setPageComponentsLoading(false);
        }

        // Only set default "Go to..." naming for new outcomes that don't have a predefined name
        if (isNullOrEmpty(outcome?.id) && isNullOrWhitespace(initialDeveloperName)) {
            const nextMapName = nextMapElementId
                ? getByID(nextMapElementId, mapElements)?.developerName
                : null;
            const name = nextMapName
                ? stringReplace(translations.OUTCOME_next_map_element_name_template, {
                      nextMapName,
                  })
                : translations.OUTCOME_next_map_element_name_default;
            setDeveloperName(name);
            setLabel(name);
        }
    }, []);

    useEffect(() => {
        setConfigTitle(
            stringReplace(translations.OUTCOME_config_title_template, {
                mapElementName: mapElement?.developerName || null,
            }),
        );

        return () => setConfigTitle(null);
    }, [mapElement?.developerName, setConfigTitle]);

    const hasBusinessRules =
        mapElement && BUSINESS_RULES_ELEMENTS.includes(mapElement.elementType.toLowerCase());
    const isOneOutcomeElement =
        mapElement && ONE_OUTCOME_ELEMENTS.includes(mapElement.elementType.toLowerCase());
    const isUIElement = mapElement && UI_ELEMENTS.includes(mapElement.elementType.toLowerCase());
    const hasFaultOutcome =
        mapElement && FAULT_OUTCOME_ELEMENTS.includes(mapElement.elementType.toLowerCase());

    const isExistingOutcome = !isNullOrEmpty(outcome?.id) && !isEmptyGuid(outcome?.id ?? '');

    // Determine if this is an approval outcome (Approve or Deny)
    const isApprovalElement = mapElement?.elementType === MAP_ELEMENT_TYPES.approval;

    // For existing outcomes, check by ID or name/order
    const { isApprove: isExistingApprove, isDeny: isExistingDeny } = getApprovalOutcomeType(
        outcome,
        mapElement,
    );

    // For new outcomes, check if we should auto-assign
    const autoAssignType =
        !isExistingOutcome && isApprovalElement ? getAutoAssignmentType(mapElement) : null;

    // Final determination - is THIS outcome the Approve or Deny one?
    const isApprove = isExistingApprove || autoAssignType === 'approve';
    const isDeny = isExistingDeny || autoAssignType === 'deny';
    const isApprovalOutcome = isApprove || isDeny;

    // Initialize with correct values for approval outcomes
    const initialDeveloperName = isApprovalOutcome
        ? isApprove
            ? 'Approve'
            : 'Deny'
        : (outcome?.developerName ?? '');

    const [developerName, setDeveloperName] = useState(initialDeveloperName);
    const [label, setLabel] = useState(outcome?.label ?? '');
    const [nextMapElementId, setNextMapElementId] = useState(outcome?.nextMapElementId ?? null);
    const [pageActionBindingType, setPageActionBindingType] = useState(
        outcome?.pageActionBindingType ?? 'SAVE',
    );
    const actionBindingTypeList = [
        { label: translations.OUTCOME_action_binding_type_save_label, value: 'SAVE' },
        { label: translations.OUTCOME_action_binding_type_no_save_label, value: 'NO_SAVE' },
        {
            label: translations.OUTCOME_action_binding_type_partial_save_label,
            value: 'PARTIAL_SAVE',
        },
    ];
    const [order, setOrder] = useState(outcome?.order ?? mapElement?.outcomes?.length ?? 0);
    const [pageActionType, setPageActionType] = useState(outcome?.pageActionType ?? null);
    const actionTypeList = [
        'Add',
        'Apply',
        'Back',
        'Cancel',
        'Close',
        'Delegate',
        'Delete',
        'Done',
        'Edit',
        'Escalate',
        'Import',
        'Insert',
        'New',
        'Next',
        'Open',
        'Query',
        'Reject',
        'Remove',
        'Save',
        'Submit',
        'Update',
        'Upsert',
    ];
    const [size, setSize] = useState(outcome?.attributes?.['size'] ?? '');
    const [uri, setUri] = useState(outcome?.attributes?.['uri'] ?? '');
    const [comparison, setComparison] = useState(outcome?.comparison ?? null);
    const [pageObjectBindingId, setPageObjectBindingId] = useState(
        outcome?.pageObjectBindingId ?? null,
    );
    const [isBulkAction, setIsBulkAction] = useState(outcome?.isBulkAction ?? false);

    const [hasSubmitted, setHasSubmitted] = useState(false);

    const isNameValid = isNullOrWhitespace(developerName) === false;
    const isLabelValid = isUIElement
        ? isNullOrWhitespace(label) === false
        : // For these elements label is not shown and so empty is valid
          true;
    const isOrderValid = isOneOutcomeElement
        ? isNullOrEmpty(order) === false
        : // For these elements order is not shown and so empty is valid
          true;

    const [className, setClassName] = useState(outcome?.className ?? '');

    const isFormValid = isNameValid && isLabelValid && isOrderValid;

    const validateSave = () => {
        setHasSubmitted(true);

        if (isFormValid) {
            const attributes: Record<string, string> = outcome?.attributes ?? {};
            if (size) {
                attributes['size'] = size;
            } else {
                delete attributes['size'];
            }
            if (uri) {
                attributes['uri'] = uri;
            } else {
                delete attributes['uri'];
            }

            let updatedOutcome = {
                ...outcome,
                developerName,
                label,
                nextMapElementId,
                pageActionBindingType,
                pageActionType,
                attributes,
                order,
                comparison,
                pageObjectBindingId,
                isBulkAction,
                className,
            };

            if (!isExistingOutcome) {
                updatedOutcome.id = crypto.randomUUID();
            }

            // Apply approval constraints if this is an approval outcome
            if (isApprovalOutcome) {
                const constraintType = isApprove ? 'approve' : 'deny';
                updatedOutcome = applyApprovalOutcomeConstraints(
                    updatedOutcome,
                    constraintType,
                ) as typeof updatedOutcome;
            }

            if (mapElement) {
                let newMapElement: MapElement = {
                    ...mapElement,
                    outcomes: (mapElement?.outcomes || [])
                        .filter((o) => o.id !== updatedOutcome.id)
                        .concat([updatedOutcome as Outcome]),
                };

                // Update the approval object with the outcome ID
                if (updatedOutcome.id && isApprovalOutcome) {
                    const constraintType = isApprove ? 'approve' : 'deny';
                    const updatedElement = updateApprovalOutcomeAssignment(
                        newMapElement,
                        updatedOutcome.id,
                        constraintType,
                    );
                    newMapElement = {
                        ...updatedElement,
                        outcomes: updatedElement.outcomes ?? [],
                    };
                }

                setMapElement(newMapElement);
                onSave(newMapElement as MapElementRequestAPI);
            }
        }
    };

    const makeFaultOutcome = () => {
        const updatedOutcome: Outcome = {
            id: FAULT_OUTCOME_ID,
            developerName: FAULT_OUTCOME_DEVELOPER_NAME,
            order,
            nextMapElementId,
            controlPoints: outcome?.controlPoints ?? null,
            developerSummary: null,
            label: null,
            pageActionType: null,
            isBulkAction: false,
            pageActionBindingType: null,
            pageObjectBindingId: null,
            comparison: null,
            flowOut: null,
            nextMapElementDeveloperName: null,
            attributes: {},
            className: null,
        };

        if (!mapElement) {
            return;
        }

        // Remove any previous fault outcome
        // Remove the original outcome being edited
        const nonFaultElements =
            mapElement.outcomes !== null
                ? (mapElement.outcomes ?? []).filter(
                      (o) => o.id !== FAULT_OUTCOME_ID && o.id !== outcome?.id,
                  )
                : [];

        const newMapElement = {
            ...mapElement,
            outcomes: [
                ...nonFaultElements,
                // Add a new fault outcome
                updatedOutcome,
            ],
        };

        setMapElement(newMapElement);
        onSave(newMapElement as MapElementRequestAPI);
    };

    const onChangePlaceOutcome = (selection: { value: string | null } | null) => {
        setPageObjectBindingId(selection?.value ?? null);
    };

    const renderBody = () => (
        <>
            {isApprovalOutcome && (
                <div className="alert alert-info approval-outcome-alert">
                    {isApprove
                        ? translations.APPROVAL_fixed_outcome_label_approve
                        : translations.APPROVAL_fixed_outcome_label_deny}
                </div>
            )}
            <NameInput
                isValid={isNameValid}
                showValidation={hasSubmitted}
                id="name-input"
                name={developerName}
                onUpdateName={isApprovalOutcome ? () => {} : setDeveloperName}
                disabled={isApprovalOutcome}
            />

            {isUIElement ? (
                <FormGroup
                    label={translations.OUTCOME_label_input_label}
                    isRequired
                    isValid={isLabelValid}
                    validationMessage={translations.OUTCOME_label_input_validation_message}
                    showValidation={hasSubmitted}
                    htmlFor="label"
                >
                    <input
                        id="label"
                        value={label}
                        onChange={({ target: { value } }) => setLabel(value)}
                        required
                        className="form-control form-control-width"
                        type="text"
                    />
                </FormGroup>
            ) : null}

            <FormGroup
                label={translations.OUTCOME_next_map_element_select_label}
                htmlFor="next-map-element"
            >
                {pageComponentsLoading ? (
                    <Loader />
                ) : (
                    <Select
                        inputId="next-map-element"
                        className="form-control-width"
                        styles={getSharedStyles<{
                            label: string | null | undefined;
                            value: string | null;
                        }>()}
                        value={{
                            label: mapElements.find((m) => m.id === nextMapElementId)
                                ?.developerName,
                            value: nextMapElementId,
                        }}
                        onChange={(selection) => setNextMapElementId(selection?.value ?? null)}
                        options={mapElements.map(({ developerName, id }) => ({
                            label: developerName,
                            value: id,
                        }))}
                        isClearable
                    />
                )}
            </FormGroup>

            {isUIElement ? (
                <FormGroup
                    label={translations.OUTCOME_page_action_binding_select_label}
                    htmlFor="page-action-binding-type"
                    isRequired
                >
                    <Select
                        inputId="page-action-binding-type"
                        className="form-control-width"
                        styles={getSharedStyles<
                            (typeof actionBindingTypeList)[number] | undefined
                        >()}
                        value={actionBindingTypeList.find((m) => m.value === pageActionBindingType)}
                        onChange={(selection) =>
                            setPageActionBindingType(selection?.value as string)
                        }
                        options={actionBindingTypeList}
                    />
                </FormGroup>
            ) : null}

            {isOneOutcomeElement ? null : (
                <FormGroup
                    label={translations.OUTCOME_order_input_label}
                    htmlFor="order"
                    isRequired
                    isValid={isOrderValid}
                    validationMessage={translations.OUTCOME_order_input_validation_message}
                    showValidation={hasSubmitted}
                >
                    <input
                        value={order}
                        onChange={({ target: { valueAsNumber } }) => setOrder(valueAsNumber)}
                        required
                        className="form-control form-control-short"
                        type="number"
                        disabled={isApprovalOutcome}
                    />
                </FormGroup>
            )}

            {mapElement &&
            [MAP_ELEMENT_TYPES.input, MAP_ELEMENT_TYPES.modal].includes(
                mapElement.elementType.toLowerCase(),
            ) ? (
                <>
                    <FormGroup
                        label={translations.OUTCOME_page_object_binding_select_label}
                        htmlFor="pageObjectBindingId"
                    >
                        <Select
                            inputId="pageObjectBindingId"
                            className="form-control-width"
                            styles={getSharedStyles<
                                (typeof pageComponentOptions)[number] | undefined
                            >()}
                            value={pageComponentOptions.find(
                                (pageComp) => pageComp.value === pageObjectBindingId,
                            )}
                            onChange={(selection) => onChangePlaceOutcome(selection ?? null)}
                            options={pageComponentOptions}
                            isClearable
                        />
                    </FormGroup>
                    <FormGroup>
                        <label htmlFor="is-bulk-action-toggle">
                            <Toggle
                                id="is-bulk-action-toggle"
                                testId="isBulkAction"
                                isOn={isBulkAction}
                                onChange={({ isOn }) => setIsBulkAction(isOn)}
                            />
                            {translations.OUTCOME_is_bulk_action_toggle_label}
                        </label>
                    </FormGroup>
                </>
            ) : null}

            {isUIElement ? (
                <>
                    <FormGroup
                        label={translations.OUTCOME_page_action_type_select_label}
                        htmlFor="page-action-type"
                    >
                        <Select
                            inputId="page-action-type"
                            className="form-control-width"
                            styles={getSharedStyles<{ label: string; value: string } | undefined>()}
                            value={actionTypeList
                                .map((value) => ({
                                    label: value,
                                    value,
                                }))
                                .find((m) => m.value === pageActionType)}
                            onChange={(selection) => setPageActionType(selection?.value ?? null)}
                            options={actionTypeList.map((value) => ({
                                label: value,
                                value,
                            }))}
                            isClearable
                        />
                    </FormGroup>

                    <FormGroup
                        label={translations.OUTCOME_CSS_classes}
                        htmlFor="outcome-className"
                        showValidation={hasSubmitted}
                    >
                        <input
                            id="outcome-className"
                            value={className}
                            onChange={({ target: { value } }) => setClassName(value)}
                            className="form-control form-control-width"
                        />
                    </FormGroup>

                    <FormGroup
                        label={translations.OUTCOME_attribute_button_size_label}
                        htmlFor="outcome-size"
                    >
                        <select
                            id="outcome-size"
                            className="form-control form-control-width"
                            onChange={({ target: { value } }) => setSize(value)}
                            value={size}
                        >
                            <option value="">
                                {translations.OUTCOME_size_attribute_value_default_label}
                            </option>
                            <option value="lg">
                                {translations.OUTCOME_size_attribute_value_large_label}
                            </option>
                            <option value="sm">
                                {translations.OUTCOME_size_attribute_value_small_label}
                            </option>
                            <option value="xs">
                                {translations.OUTCOME_size_attribute_value_extra_small_label}
                            </option>
                        </select>
                    </FormGroup>

                    <FormGroup
                        label={translations.OUTCOME_attribute_button_uri_label}
                        htmlFor="outcome-uri"
                        className="margin-bottom-none"
                    >
                        <input
                            id="outcome-uri"
                            value={uri}
                            onChange={({ target: { value } }) => setUri(value)}
                            className="form-control form-control-width"
                            type="text"
                            placeholder={'https://boomi.com'}
                        />
                        <span className="help-block margin-bottom-none">
                            {translations.OUTCOME_attribute_button_uri_help_text}
                        </span>
                    </FormGroup>
                </>
            ) : null}

            {hasBusinessRules ? (
                <BusinessRules
                    container={container}
                    comparison={comparison}
                    setComparison={setComparison}
                />
            ) : null}

            {hasFaultOutcome ? (
                <FormGroup>
                    <ButtonPrimary onClick={makeFaultOutcome} testId="fault">
                        {translations.OUTCOME_use_outcome_for_faults}
                    </ButtonPrimary>
                </FormGroup>
            ) : null}
        </>
    );

    const renderFooter = () => (
        <>
            <ButtonDefault className="flex-child-right" onClick={onCancel} data-testid="cancel">
                {translations.COMMON_cancel}
            </ButtonDefault>
            <ButtonPrimary className="margin-left" onClick={validateSave} data-testid="save">
                {isExistingOutcome ? translations.COMMON_update : translations.COMMON_add}
            </ButtonPrimary>
        </>
    );

    return (
        <>
            <ModalBody>{renderBody()}</ModalBody>
            <ModalFooter>{renderFooter()}</ModalFooter>
        </>
    );
};

export default OutcomeDetails;
